import { useEffect, useState } from 'react';

import { App, Button, Card, Descriptions, Skeleton, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import Layout from 'components/Layout/Layout';

import { ApiErrorData } from 'models/ApiResponse';
import { OperationRequest } from 'models/OperationRequest';

import {
  OperationRequirementService,
  pctOf,
} from 'services/OperationRequirementService';

import './OperationRequestDetail.scss';

function OperationRequestDetail() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const i18nBase = 'scenes.requirements_operation.components.detail';

  const [loading, setLoading] = useState(true);
  const [request, setRequest] = useState<OperationRequest | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const response = await OperationRequirementService.getById(id);
      if (cancelled) return;
      setLoading(false);
      if (response.success) {
        setRequest(response.data as OperationRequest);
      } else {
        const error = response.data as ApiErrorData;
        message.error(
          (error.message as string) || t(`${i18nBase}.errors.not_found`),
          3.5
        );
        setRequest(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, message, t, i18nBase]);

  return (
    <Layout title="">
      <div className="operation-request-detail">
        <div className="operation-request-detail__header">
          <div>
            <h1 className="operation-request-detail__title">
              {request?.id || id}
            </h1>
            <p className="operation-request-detail__subtitle">
              {t(`${i18nBase}.subtitle`)}
            </p>
          </div>
          <Button onClick={() => navigate('/portal-go/requirements-review')}>
            {t(`${i18nBase}.buttons.back`)}
          </Button>
        </div>

        <Card bordered className="operation-request-detail__card">
          {loading ? (
            <Skeleton active paragraph={{ rows: 6 }} />
          ) : request ? (
            <Descriptions column={{ xs: 1, sm: 2 }} bordered size="middle">
              <Descriptions.Item label={t(`${i18nBase}.labels.brand`)}>
                {request.brand}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.campaign`)}>
                {request.campaign}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.week`)}>
                {request.week}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.status`)}>
                <Tag>
                  {t(
                    `scenes.requirements_operation.scene.status.${request.status}`
                  )}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.profile`)}>
                {request.profile}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.contract`)}>
                {t(
                  `scenes.requirements_operation.scene.contract.${request.type}`
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.shifts`)}>
                {request.turnos} / {request.slots}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.assignment`)}>
                {pctOf(request)}% ({request.filled}/{request.slots})
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.supervisor`)}>
                {request.supervisor}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.kam`)}>
                {request.kam}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.stores`)}>
                {request.stores}
                {request.vipStores > 0 ? ` (${request.vipStores} VIP)` : ''}
              </Descriptions.Item>
              <Descriptions.Item label={t(`${i18nBase}.labels.start`)}>
                {request.startDate}
              </Descriptions.Item>
            </Descriptions>
          ) : (
            <p>{t(`${i18nBase}.errors.not_found`)}</p>
          )}
        </Card>
      </div>
    </Layout>
  );
}

export default OperationRequestDetail;
